import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, useToastStore } from '../../store';
import {
  getAdminUsers,
  getAdminUserDetail,
  updateAdminUser,
  deleteAdminUser,
  adminAddCredits,
  retryAdminCreditGrantEmail,
  type AdminUserSortDirection,
  type AdminUserSortKey,
} from '../../api';
import { AdminUserDetail, AdminUserSummary } from '../../types';
import { AdminLayout } from '../../components/admin/AdminLayout';

export function AdminUsers() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuthStore();
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<AdminUserSortKey>('created_at');
  const [sortDirection, setSortDirection] = useState<AdminUserSortDirection>('desc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<AdminUserDetail | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingUser, setDeletingUser] = useState<{ id: string; email: string } | null>(null);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [creditUser, setCreditUser] = useState<{ id: string; email: string; balance_micros: number } | null>(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditNote, setCreditNote] = useState('');
  const [notifyCreditUser, setNotifyCreditUser] = useState(true);
  const [creditEmailFailure, setCreditEmailFailure] = useState<{
    ledgerId: string;
    attempts: number;
    error: string;
  } | null>(null);
  const [isAddingCredits, setIsAddingCredits] = useState(false);
  const addToast = useToastStore((state) => state.addToast);
  const limit = 20;

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !user?.is_admin)) {
      navigate('/', { replace: true });
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  useEffect(() => {
    if (isAuthenticated && user?.is_admin) {
      loadUsers();
    }
  }, [isAuthenticated, user, page, search, sortBy, sortDirection]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAdminUsers(
        limit,
        page * limit,
        search,
        sortBy,
        sortDirection
      );
      setUsers(data.users);
      setTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deletingUser) return;
    try {
      setIsDeleting(true);
      await deleteAdminUser(deletingUser.id);
      setDeletingUser(null);
      setDeleteConfirmInput('');
      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddCredits = async () => {
    if (!creditUser) return;
    const amount = parseFloat(creditAmount);
    if (isNaN(amount) || amount === 0) return;
    try {
      setIsAddingCredits(true);
      setError(null);
      const result = await adminAddCredits(
        creditUser.id,
        amount,
        creditNote || undefined,
        notifyCreditUser
      );
      await loadUsers();

      if (notifyCreditUser && result.email_notification.status !== 'accepted') {
        setCreditUser({ ...creditUser, balance_micros: result.balance_micros });
        setCreditEmailFailure({
          ledgerId: result.ledger_id,
          attempts: result.email_notification.attempts,
          error: result.email_notification.error || 'Unknown Resend error',
        });
        addToast({
          type: 'warning',
          title: 'Credits added; email failed',
          message: `Resend failed after ${result.email_notification.attempts} attempt(s): ${result.email_notification.error || 'unknown error'}. Do not add the credits again.`,
          duration: 0,
        });
      } else {
        closeCreditModal();
        addToast({
          type: 'success',
          title: 'Credits added',
          message: notifyCreditUser
            ? `The credits were added and Resend accepted the email to ${creditUser.email}.`
            : `The credits were added to ${creditUser.email}.`,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add credits');
    } finally {
      setIsAddingCredits(false);
    }
  };

  const handleRetryCreditEmail = async () => {
    if (!creditUser || !creditEmailFailure) return;
    try {
      setIsAddingCredits(true);
      setError(null);
      const result = await retryAdminCreditGrantEmail(
        creditUser.id,
        creditEmailFailure.ledgerId
      );
      if (result.email_notification.status === 'accepted') {
        const recipientEmail = creditUser.email;
        closeCreditModal();
        addToast({
          type: 'success',
          title: 'Email accepted',
          message: `Resend accepted the credit notification to ${recipientEmail}.`,
        });
        return;
      }

      setCreditEmailFailure({
        ledgerId: result.ledger_id,
        attempts: result.email_notification.attempts,
        error: result.email_notification.error || 'Unknown Resend error',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to retry credit email');
    } finally {
      setIsAddingCredits(false);
    }
  };

  const closeCreditModal = () => {
    setCreditUser(null);
    setCreditAmount('');
    setCreditNote('');
    setNotifyCreditUser(true);
    setCreditEmailFailure(null);
  };

  const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPage(0);
    setSearch(searchInput.trim());
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(0);
  };

  const handleSort = (key: AdminUserSortKey) => {
    setPage(0);
    if (sortBy === key) {
      setSortDirection((current) => current === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortBy(key);
    setSortDirection(defaultSortDirection(key));
  };

  const formatCost = (micros: number) => `$${(micros / 1_000_000).toFixed(4)}`;
  const formatDate = (date: string | null) => date ? new Date(date).toLocaleDateString() : '-';
  const formatDateTime = (date: string | null) => date ? new Date(date).toLocaleString() : '-';

  const openEditUser = async (userId: string) => {
    try {
      setEditLoading(true);
      setEditError(null);
      const detail = await getAdminUserDetail(userId);
      setEditingUser({
        ...detail,
        is_admin: !!detail.is_admin,
        email_verified: !!detail.email_verified,
      });
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Failed to load user');
    } finally {
      setEditLoading(false);
    }
  };

  const closeEditUser = () => {
    setEditingUser(null);
    setEditError(null);
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;
    try {
      setIsSaving(true);
      setEditError(null);
      await updateAdminUser(editingUser.id, {
        email: editingUser.email,
        display_name: editingUser.display_name,
        is_admin: !!editingUser.is_admin,
        email_verified: editingUser.email_verified,
      });
      await loadUsers();
      closeEditUser();
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Failed to update user');
    } finally {
      setIsSaving(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-900">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (!user?.is_admin) return null;

  return (
    <AdminLayout
      title="Users"
      breadcrumbs={[{ label: 'Admin', to: '/admin' }, { label: 'Users' }]}
      actions={<span className="text-dark-400">{total} total</span>}
    >
      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      <form onSubmit={handleSearch} className="mb-4 flex flex-wrap items-center gap-2">
        <label htmlFor="admin-user-search" className="sr-only">Filter users</label>
        <input
          id="admin-user-search"
          type="search"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Filter by email or display name"
          className="w-full sm:w-80 bg-dark-800 border border-dark-600 rounded px-3 py-2 text-dark-50 placeholder:text-dark-500"
        />
        <button type="submit" className="btn-primary">Search</button>
        {(search || searchInput) && (
          <button type="button" onClick={clearSearch} className="btn-secondary">Clear</button>
        )}
        {search && (
          <span className="text-sm text-dark-400">
            {total} {total === 1 ? 'match' : 'matches'} for “{search}”
          </span>
        )}
      </form>

      <div className="bg-dark-800 border border-dark-600 rounded-lg overflow-x-auto">
        <table className="w-full">
          <thead className="bg-dark-700">
            <tr>
              <SortableTh colKey="email" active={sortBy} direction={sortDirection} onSort={handleSort}>User</SortableTh>
              <SortableTh colKey="world_count" active={sortBy} direction={sortDirection} onSort={handleSort}>Worlds</SortableTh>
              <SortableTh colKey="message_count" active={sortBy} direction={sortDirection} onSort={handleSort}>Messages</SortableTh>
              <SortableTh colKey="total_tokens" active={sortBy} direction={sortDirection} onSort={handleSort}>Tokens</SortableTh>
              <SortableTh colKey="total_cost_micros" active={sortBy} direction={sortDirection} onSort={handleSort}>COGS</SortableTh>
              <SortableTh colKey="total_billed_micros" active={sortBy} direction={sortDirection} onSort={handleSort}>Billed</SortableTh>
              <SortableTh colKey="total_profit_micros" active={sortBy} direction={sortDirection} onSort={handleSort}>Profit</SortableTh>
              <SortableTh colKey="balance_micros" active={sortBy} direction={sortDirection} onSort={handleSort}>Balance</SortableTh>
              <SortableTh colKey="last_active" active={sortBy} direction={sortDirection} onSort={handleSort}>Last Active</SortableTh>
              <SortableTh colKey="last_logged_in_at" active={sortBy} direction={sortDirection} onSort={handleSort}>Last Logged In</SortableTh>
              <SortableTh colKey="created_at" active={sortBy} direction={sortDirection} onSort={handleSort}>Joined</SortableTh>
              <th className="text-left px-4 py-3 text-dark-300 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={12} className="px-4 py-8 text-center text-dark-400">
                  Loading...
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={12} className="px-4 py-8 text-center text-dark-400">
                  {search ? 'No users match this filter' : 'No users found'}
                </td>
              </tr>
            ) : (
              users.map((u) => (
                <tr key={u.id} className="border-t border-dark-600 hover:bg-dark-700">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-dark-50">{u.email}</span>
                      {u.is_admin ? (
                        <span className="text-xs bg-primary-500/20 text-primary-400 px-2 py-0.5 rounded">
                          Admin
                        </span>
                      ) : null}
                    </div>
                    {u.display_name && (
                      <div className="text-sm text-dark-400">{u.display_name}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-dark-200">{u.world_count}</td>
                  <td className="px-4 py-3 text-dark-200">{u.message_count.toLocaleString()}</td>
                  <td className="px-4 py-3 text-dark-200">{u.total_tokens.toLocaleString()}</td>
                  <td className="px-4 py-3 text-dark-200">{formatCost(u.total_cost_micros)}</td>
                  <td className="px-4 py-3 text-dark-200">{formatCost(u.total_billed_micros)}</td>
                  <td className="px-4 py-3 text-dark-200">{formatCost(u.total_profit_micros)}</td>
                  <td className="px-4 py-3 text-dark-200">{formatCost(u.balance_micros)}</td>
                  <td className="px-4 py-3 text-dark-400 text-sm whitespace-nowrap">{formatDateTime(u.last_active)}</td>
                  <td className="px-4 py-3 text-dark-400 text-sm whitespace-nowrap">{formatDateTime(u.last_logged_in_at)}</td>
                  <td className="px-4 py-3 text-dark-400 text-sm">{formatDate(u.created_at)}</td>
                  <td className="px-4 py-3 flex items-center gap-3">
                    <button
                      onClick={() => openEditUser(u.id)}
                      className="text-sm text-primary-400 hover:text-primary-300"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        setCreditUser({ id: u.id, email: u.email, balance_micros: u.balance_micros });
                        setCreditAmount('');
                        setCreditNote('');
                        setNotifyCreditUser(true);
                        setCreditEmailFailure(null);
                      }}
                      className="text-sm text-green-400 hover:text-green-300"
                    >
                      Credits
                    </button>
                    {u.id !== user?.id && (
                      <button
                        onClick={() => { setDeletingUser({ id: u.id, email: u.email }); setDeleteConfirmInput(''); }}
                        className="text-sm text-red-400 hover:text-red-300"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-between mt-4">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="btn-secondary disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-dark-400">
            Page {page + 1} of {Math.ceil(total / limit)}
          </span>
          <button
            onClick={() => setPage(p => p + 1)}
            disabled={(page + 1) * limit >= total}
            className="btn-secondary disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-dark-800 border border-dark-600 rounded-lg w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-dark-50">Edit User</h2>
              <button onClick={closeEditUser} className="text-dark-400 hover:text-dark-50">✕</button>
            </div>

            {editError && (
              <div className="bg-red-900/50 border border-red-500 text-red-200 px-3 py-2 rounded mb-4">
                {editError}
              </div>
            )}

            {editLoading ? (
              <div className="text-center text-dark-400 py-6">Loading user...</div>
            ) : (
              <div className="space-y-4">
                <label className="block">
                  <span className="text-sm text-dark-300">Email</span>
                  <input
                    type="email"
                    value={editingUser.email}
                    onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                    className="mt-1 w-full bg-dark-700 border border-dark-600 rounded px-3 py-2 text-dark-50"
                  />
                </label>
                <label className="block">
                  <span className="text-sm text-dark-300">Display name</span>
                  <input
                    type="text"
                    value={editingUser.display_name || ''}
                    onChange={(e) => setEditingUser({ ...editingUser, display_name: e.target.value || null })}
                    className="mt-1 w-full bg-dark-700 border border-dark-600 rounded px-3 py-2 text-dark-50"
                  />
                </label>
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 text-sm text-dark-300">
                    <input
                      type="checkbox"
                      checked={!!editingUser.is_admin}
                      onChange={(e) => setEditingUser({ ...editingUser, is_admin: e.target.checked })}
                      className="accent-primary-500"
                    />
                    Admin user
                  </label>
                  <label className="flex items-center gap-2 text-sm text-dark-300">
                    <input
                      type="checkbox"
                      checked={!!editingUser.email_verified}
                      onChange={(e) => setEditingUser({ ...editingUser, email_verified: e.target.checked })}
                      className="accent-primary-500"
                    />
                    Email verified
                  </label>
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button onClick={closeEditUser} className="btn-secondary">Cancel</button>
              <button onClick={handleSaveUser} disabled={isSaving || editLoading} className="btn-primary disabled:opacity-50">
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Add Credits Modal */}
      {creditUser && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-dark-800 border border-dark-600 rounded-lg w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-dark-50">Add Credits</h2>
              <button onClick={closeCreditModal} className="text-dark-400 hover:text-dark-50">✕</button>
            </div>

            <p className="text-dark-300 text-sm mb-1">
              User: <span className="text-dark-50">{creditUser.email}</span>
            </p>
            <p className="text-dark-300 text-sm mb-4">
              Current balance: <span className="text-dark-50">{formatCost(creditUser.balance_micros)}</span>
            </p>

            {creditEmailFailure ? (
              <div className="rounded border border-amber-500/50 bg-amber-950/30 p-4">
                <p className="text-sm font-medium text-amber-200">
                  The credits were added, but Resend did not accept the email.
                </p>
                <p className="mt-2 break-words text-xs text-amber-100/80">
                  {creditEmailFailure.error}
                </p>
                <p className="mt-2 text-xs text-dark-300">
                  Retrying below sends only the existing notification; it does not add credits again.
                </p>
              </div>
            ) : (
              <>
                <label className="block mb-4">
                  <span className="text-sm text-dark-300">Amount (USD)</span>
                  <input
                    type="number"
                    step="0.01"
                    value={creditAmount}
                    onChange={(e) => {
                      setCreditAmount(e.target.value);
                      setNotifyCreditUser(parseFloat(e.target.value) > 0);
                    }}
                    placeholder="e.g. 5.00 or -1.50"
                    className="mt-1 w-full bg-dark-700 border border-dark-600 rounded px-3 py-2 text-dark-50"
                  />
                  <span className="text-xs text-dark-400 mt-1 block">
                    Positive to add, negative to deduct
                  </span>
                </label>

                <label className="block">
                  <span className="text-sm text-dark-300">Note (optional)</span>
                  <input
                    type="text"
                    value={creditNote}
                    onChange={(e) => setCreditNote(e.target.value)}
                    placeholder="e.g. Billing correction"
                    className="mt-1 w-full bg-dark-700 border border-dark-600 rounded px-3 py-2 text-dark-50"
                  />
                </label>

                <label className={`mt-4 flex items-start gap-3 ${parseFloat(creditAmount) > 0 ? 'text-dark-200' : 'text-dark-500'}`}>
                  <input
                    type="checkbox"
                    checked={notifyCreditUser}
                    onChange={(e) => setNotifyCreditUser(e.target.checked)}
                    disabled={!(parseFloat(creditAmount) > 0)}
                    className="mt-0.5 accent-primary-500 disabled:opacity-50"
                  />
                  <span>
                    <span className="block text-sm">Email the user about these free credits (recommended)</span>
                    <span className="block text-xs mt-1">
                      Sends a notification that the Administrator added this amount for them to try the platform.
                    </span>
                  </span>
                </label>
              </>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button onClick={closeCreditModal} className="btn-secondary">Cancel</button>
              {creditEmailFailure ? (
                <button
                  onClick={handleRetryCreditEmail}
                  disabled={isAddingCredits}
                  className="btn-primary disabled:opacity-50"
                >
                  {isAddingCredits ? 'Retrying...' : 'Retry Email Only'}
                </button>
              ) : (
                <button
                  onClick={handleAddCredits}
                  disabled={isAddingCredits || !creditAmount || parseFloat(creditAmount) === 0 || isNaN(parseFloat(creditAmount))}
                  className="btn-primary disabled:opacity-50"
                >
                  {isAddingCredits ? 'Adding...' : 'Add Credits'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Delete User Confirmation Modal */}
      {deletingUser && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-dark-800 border border-dark-600 rounded-lg w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-red-400">Delete User</h2>
              <button onClick={() => { setDeletingUser(null); setDeleteConfirmInput(''); }} className="text-dark-400 hover:text-dark-50">✕</button>
            </div>

            <p className="text-dark-200 mb-2">
              This will <span className="text-red-400 font-semibold">permanently delete</span> the user <span className="text-dark-50 font-mono">{deletingUser.email}</span> and all their data including worlds, messages, characters, and inference logs.
            </p>
            <p className="text-dark-400 text-sm mb-4">This action cannot be undone.</p>

            <label className="block">
              <span className="text-sm text-dark-300">Type the user's email to confirm:</span>
              <input
                type="text"
                value={deleteConfirmInput}
                onChange={(e) => setDeleteConfirmInput(e.target.value)}
                placeholder={deletingUser.email}
                className="mt-1 w-full bg-dark-700 border border-dark-600 rounded px-3 py-2 text-dark-50"
              />
            </label>

            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => { setDeletingUser(null); setDeleteConfirmInput(''); }} className="btn-secondary">Cancel</button>
              <button
                onClick={handleDeleteUser}
                disabled={deleteConfirmInput !== deletingUser.email || isDeleting}
                className="px-4 py-2 bg-red-600 hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded transition-colors"
              >
                {isDeleting ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function SortableTh({
  children,
  colKey,
  active,
  direction,
  onSort,
}: {
  children: React.ReactNode;
  colKey: AdminUserSortKey;
  active: AdminUserSortKey;
  direction: AdminUserSortDirection;
  onSort: (key: AdminUserSortKey) => void;
}) {
  const isActive = active === colKey;
  return (
    <th
      className={`text-left px-4 py-3 font-medium whitespace-nowrap ${isActive ? 'text-primary-300' : 'text-dark-300'}`}
      aria-sort={isActive ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(colKey)}
        className="flex items-center gap-1 cursor-pointer select-none hover:text-dark-100"
      >
        {children}
        <span aria-hidden="true" className={isActive ? '' : 'text-dark-500'}>
          {isActive ? (direction === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
}

function defaultSortDirection(key: AdminUserSortKey): AdminUserSortDirection {
  return key === 'email' ? 'asc' : 'desc';
}
